import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  DndContext, useDraggable, useDroppable, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  Loader2, RefreshCw, Sparkles, Search, Bus, MapPin, Activity, Zap, Wifi, WifiOff,
  Clock, CheckCircle2, Filter, ArrowRight, X, Brain, Lock, Unlock, Edit3, Info,
} from "lucide-react";
import { MapContainer, TileLayer, Marker, Circle } from "react-leaflet";
import L from "leaflet";

const MAX_DISPLAY = 50;
const AUTO_THRESHOLD = 0.9; // 90 %

const GPS_BADGE = {
  live: { icon: Wifi, color: "bg-green-500", text: "text-white", label: "GPS LIVE" },
  stale: { icon: Clock, color: "bg-orange-400", text: "text-zinc-900", label: "GPS ANCIEN" },
  offline: { icon: WifiOff, color: "bg-red-500", text: "text-white", label: "OFFLINE" },
  no_gps: { icon: WifiOff, color: "bg-zinc-300", text: "text-zinc-700", label: "PAS DE GPS" },
};

function GpsBadge({ status }) {
  const meta = GPS_BADGE[status] || GPS_BADGE.no_gps;
  const Icon = meta.icon;
  return (
    <span data-testid={`gps-badge-${status}`}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest border border-zinc-900 ${meta.color} ${meta.text}`}>
      <Icon size={9} /> {meta.label}
    </span>
  );
}

function ConfidencePill({ value }) {
  const pct = Math.round((value || 0) * 100);
  const tier = pct >= 80 ? "high" : pct >= 50 ? "medium" : pct >= 20 ? "low" : "none";
  const styles = {
    high: "bg-green-500 text-white border-green-700",
    medium: "bg-yellow-300 text-zinc-900 border-yellow-700",
    low: "bg-orange-300 text-zinc-900 border-orange-700",
    none: "bg-zinc-200 text-zinc-700 border-zinc-400",
  };
  return (
    <span data-testid={`confidence-${tier}`}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-mono font-bold border ${styles[tier]}`}>
      {pct}%
    </span>
  );
}

const SAEIV_BADGE = {
  normal:           { color: "bg-green-500",   text: "text-white",     label: "NORMAL" },
  retard:           { color: "bg-orange-500",  text: "text-white",     label: "RETARD" },
  hors_tournee:     { color: "bg-red-500",     text: "text-white",     label: "HORS TOURNÉE" },
  gps_incoherent:   { color: "bg-purple-500",  text: "text-white",     label: "GPS INCOHÉRENT" },
  offline:          { color: "bg-zinc-400",    text: "text-white",     label: "OFFLINE" },
  parked:           { color: "bg-blue-500",    text: "text-white",     label: "STATIONNÉ" },
};

function SaeivBadge({ state }) {
  const meta = SAEIV_BADGE[state] || SAEIV_BADGE.offline;
  return (
    <span data-testid={`saeiv-state-${state}`}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest border border-zinc-900 ${meta.color} ${meta.text}`}>
      {meta.label}
    </span>
  );
}

function formatEta(seconds) {
  if (!seconds && seconds !== 0) return "—";
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)}h${String(m % 60).padStart(2, "0")}`;
}

const SOURCE_BADGE = {
  ai:       { color: "bg-blue-500 text-white",     label: "IA" },
  manual:   { color: "bg-zinc-700 text-white",     label: "MANUEL" },
  imported: { color: "bg-purple-500 text-white",   label: "IMPORTÉ" },
  none:     { color: "bg-zinc-200 text-zinc-700",  label: "—" },
};

function SourceBadge({ source, locked }) {
  if (locked) {
    return (
      <span data-testid="source-locked"
        className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest border border-zinc-900 bg-[#FFC60A] text-zinc-900">
        <Lock size={9} /> VERROUILLÉ
      </span>
    );
  }
  const meta = SOURCE_BADGE[source] || SOURCE_BADGE.none;
  return (
    <span data-testid={`source-${source}`}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest border border-zinc-900 ${meta.color}`}>
      {meta.label}
    </span>
  );
}

const busDivIcon = L.divIcon({
  className: "",
  html: `<div style="background:#0A0A0B;color:#FFC60A;font-weight:900;border-radius:999px;padding:4px 8px;border:1px solid #0A0A0B;font-family:'Space Grotesk',monospace;font-size:11px;">BUS</div>`,
  iconSize: [40, 22],
  iconAnchor: [20, 11],
});
const stopDivIcon = L.divIcon({
  className: "",
  html: `<div style="background:#22c55e;width:10px;height:10px;border-radius:999px;border:2px solid #064e3b;"></div>`,
  iconSize: [10, 10],
  iconAnchor: [5, 5],
});

function VehicleMiniMap({ vehicle, candidate }) {
  if (!vehicle || typeof vehicle.lat !== "number" || typeof vehicle.lon !== "number") {
    return (
      <div className="h-40 flex items-center justify-center bg-zinc-50 text-xs text-zinc-500 border border-zinc-200">
        Aucune position GPS récente
      </div>
    );
  }
  return (
    <div className="h-40 border border-zinc-900 overflow-hidden" data-testid="vehicle-mini-map">
      <MapContainer center={[vehicle.lat, vehicle.lon]} zoom={13} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false} attributionControl={false}>
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <Marker position={[vehicle.lat, vehicle.lon]} icon={busDivIcon} />
        {candidate?.nearest_stop_name && (
          <Circle center={[vehicle.lat, vehicle.lon]} radius={Math.min(candidate.distance_m || 0, 4000)}
            pathOptions={{ color: "#FFC60A", fillOpacity: 0.05, weight: 2, dashArray: "4 4" }} />
        )}
      </MapContainer>
    </div>
  );
}

// ─── Draggable vehicle card ─────────────────────────────────────────────────
function DraggableVehicle({ v, sv, isSelected, onSelect, onAccept }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `veh-${v.vehicle_id}`,
    data: { vehicleId: v.vehicle_id, busName: v.bus_name },
  });
  const top = v.candidates?.[0];
  const has = v.candidates?.length > 0;
  const detectedRoute = sv?.detected_route_short_name;
  const detectedTrip = sv?.detected_trip_id;
  const operationalState = sv?.operational_state || (v.gps_status === "offline" ? "offline" : "offline");
  return (
    <div ref={setNodeRef}
      data-testid={`vehicle-card-${v.vehicle_id}`}
      className={`border ${isSelected ? "border-[#FFC60A] border-2 shadow-[3px_3px_0_0_#0A0A0B]" : "border-zinc-900"} bg-white p-3 mb-2 transition-all ${isDragging ? "opacity-40" : ""}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div {...listeners} {...attributes}
            className="cursor-grab active:cursor-grabbing flex items-center flex-wrap gap-1.5 mb-1">
            <Bus size={14} className="text-zinc-500" />
            <span className="font-display font-black text-sm truncate" title={v.bus_name}>{v.bus_name}</span>
            <GpsBadge status={v.gps_status} />
            {sv && <SaeivBadge state={operationalState} />}
            {sv && (sv.is_locked || sv.affectation_source !== "none") && (
              <SourceBadge source={sv.affectation_source} locked={sv.is_locked} />
            )}
          </div>
          <div className="text-[10px] font-mono text-zinc-500 flex flex-wrap gap-2 mt-1">
            {typeof v.lat === "number" && <span><MapPin size={9} className="inline" /> {v.lat.toFixed(3)}, {v.lon.toFixed(3)}</span>}
            {typeof v.speed === "number" && <span><Activity size={9} className="inline" /> {Math.round(v.speed)} km/h</span>}
            {v.gps_age_seconds != null && <span><Clock size={9} className="inline" /> {Math.floor(v.gps_age_seconds / 60)} min</span>}
            {sv?.heading_deg != null && <span title="Cap (degrés)">↗ {Math.round(sv.heading_deg)}°</span>}
            {v.last_detected_route_id && <span className="text-blue-700">⏵ Hist : {v.last_detected_route_id}</span>}
          </div>
        </div>
        <button onClick={() => onSelect(v)} data-testid={`open-detail-${v.vehicle_id}`}
          className="text-[10px] font-mono uppercase tracking-widest border border-zinc-900 px-1.5 py-0.5 hover:bg-zinc-900 hover:text-white">
          Détail
        </button>
      </div>

      {/* SAEIV — Tournée détectée live */}
      {detectedRoute && (
        <div className="mt-2 border-t border-zinc-200 pt-2 bg-green-50 -mx-3 -mb-3 px-3 pb-2 border-b border-green-700" data-testid={`saeiv-detected-${v.vehicle_id}`}>
          <div className="flex items-center justify-between text-[9px] font-mono tracking-widest uppercase text-green-800 mb-1">
            <span><Zap size={9} className="inline" /> Tournée détectée IA</span>
            <ConfidencePill value={sv.confidence} />
          </div>
          <div className="flex items-center justify-between flex-wrap gap-1">
            <div className="text-xs font-semibold">
              Ligne <span className="font-display font-black">{detectedRoute}</span>
              {sv.detected_headsign && <span className="text-[10px] font-mono text-zinc-600 ml-1">→ {sv.detected_headsign}</span>}
            </div>
            <div className="text-[10px] font-mono">
              {sv.next_stop_name && <span>📍 {sv.next_stop_name} · ETA {formatEta(sv.next_stop_eta_seconds)}</span>}
              {sv.delay_minutes != null && sv.delay_minutes > 0 && <span className="ml-1 text-orange-700 font-bold">+{sv.delay_minutes}min</span>}
            </div>
          </div>
          <div className="text-[9px] font-mono text-zinc-500 mt-1 truncate" title={detectedTrip || ""}>Trip : {detectedTrip}</div>
        </div>
      )}

      {/* Fallback : suggestion IA quand SAEIV n'a pas détecté de trip */}
      {!detectedRoute && has && (
        <div className="mt-2 border-t border-zinc-200 pt-2">
          <div className="flex items-center justify-between text-[9px] font-mono tracking-widest uppercase text-zinc-500 mb-1">
            <span><Brain size={9} className="inline" /> Suggestion IA</span>
            {top && <ConfidencePill value={top.confidence} />}
          </div>
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold">
              → Ligne <span className="font-display font-black">{top.route_short_name}</span>
              <span className="text-[10px] font-mono text-zinc-500 ml-1">· {Math.round(top.distance_m)}m</span>
            </div>
            <button onClick={() => onAccept(v, top)}
              data-testid={`accept-suggestion-${v.vehicle_id}`}
              className="text-[10px] font-mono uppercase tracking-widest border border-zinc-900 bg-[#FFC60A] px-2 py-0.5 hover:bg-zinc-900 hover:text-[#FFC60A]">
              Accepter
            </button>
          </div>
          {v.candidates.length > 1 && (
            <div className="mt-1 text-[9px] font-mono text-zinc-500">
              Aussi : {v.candidates.slice(1).map((c) => `${c.route_short_name} (${Math.round(c.confidence * 100)}%)`).join(" · ")}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Droppable route card ───────────────────────────────────────────────────
function DroppableRoute({ route, count, onClick }) {
  const { setNodeRef, isOver } = useDroppable({ id: `route-${route.route_id}`, data: { routeId: route.route_id, routeShortName: route.route_short_name } });
  return (
    <div ref={setNodeRef}
      onClick={onClick}
      data-testid={`route-target-${route.route_id}`}
      className={`border-2 p-3 mb-2 cursor-pointer transition-all ${isOver ? "border-[#FFC60A] bg-yellow-50 shadow-[4px_4px_0_0_#FFC60A]" : "border-zinc-900 bg-white hover:bg-zinc-50"}`}>
      <div className="flex items-start gap-2">
        <div className="w-12 h-12 flex items-center justify-center font-display font-black border border-zinc-900 bg-[#FFC60A] text-zinc-900">
          {route.route_short_name}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-sm truncate">{route.route_long_name}</div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-1">
            {count} bus affecté{count > 1 ? "s" : ""}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AffectationsBulk() {
  const [data, setData] = useState({ suggestions: [], generated_at: null });
  const [saeiv, setSaeiv] = useState({ states: [], summary: {} });
  const [routes, setRoutes] = useState([]);
  const [affectations, setAffectations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | unassigned | low | offline | live | retard | hors_tournee
  const [routeFilter, setRouteFilter] = useState("");
  const [freshOnly, setFreshOnly] = useState(false); // show all by default — admin can still dispatch by clicking
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const refreshTimer = useRef(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, r, a, sv] = await Promise.all([
        api.get(`/logibus/affectations/suggestions?fresh_only=${freshOnly}&only_unassigned=false`),
        api.get("/logibus/transit/routes"),
        api.get("/logibus/affectations/active"),
        api.get("/logibus/saeiv/status"),
      ]);
      setData(s.data);
      setRoutes(r.data || []);
      setAffectations(a.data || []);
      setSaeiv(sv.data || { states: [], summary: {} });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  }, [freshOnly]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    // Auto-refresh every 30 s — keep suggestions in sync with live GPS
    refreshTimer.current = setInterval(() => { load(); }, 30000);
    return () => clearInterval(refreshTimer.current);
  }, [load]);

  const busPerRoute = useMemo(() => {
    const m = {};
    affectations.forEach((a) => {
      const rid = a.trip?.route_id;
      if (!rid) return;
      m[rid] = (m[rid] || 0) + 1;
    });
    return m;
  }, [affectations]);

  // vehicle_id → active affectation (used by Phase 2 lock/force buttons)
  const affectationByVehicle = useMemo(() => {
    const m = {};
    affectations.forEach((a) => { if (a.vehicle_id) m[a.vehicle_id] = a; });
    return m;
  }, [affectations]);

  // SAEIV state lookup by vehicle_id — merged into each card
  const saeivByVehicle = useMemo(() => {
    const m = {};
    (saeiv.states || []).forEach((s) => { m[s.vehicle_id] = s; });
    return m;
  }, [saeiv]);

  const filteredVehicles = useMemo(() => {
    const q = search.toLowerCase();
    return (data.suggestions || []).filter((v) => {
      const sv = saeivByVehicle[v.vehicle_id];
      if (q && !(v.bus_name || "").toLowerCase().includes(q) && !(v.plate || "").toLowerCase().includes(q)) return false;
      if (filter === "unassigned" && v.has_active_affectation) return false;
      if (filter === "low") {
        const top = v.candidates?.[0];
        if (!top || top.confidence >= 0.5) return false;
      }
      if (filter === "offline" && v.gps_status !== "offline") return false;
      if (filter === "live" && v.gps_status !== "live") return false;
      if (filter === "retard" && sv?.operational_state !== "retard") return false;
      if (filter === "hors_tournee" && sv?.operational_state !== "hors_tournee") return false;
      if (filter === "gps_incoherent" && sv?.operational_state !== "gps_incoherent") return false;
      if (routeFilter && (!v.candidates?.length || v.candidates[0].route_id !== routeFilter)) return false;
      return true;
    }).slice(0, MAX_DISPLAY);
  }, [data.suggestions, search, filter, routeFilter, saeivByVehicle]);

  const counters = useMemo(() => {
    const all = data.suggestions || [];
    return {
      total: all.length,
      live: all.filter((v) => v.gps_status === "live").length,
      stale: all.filter((v) => v.gps_status === "stale").length,
      offline: all.filter((v) => v.gps_status === "offline").length,
      high: all.filter((v) => v.candidates?.[0]?.confidence >= AUTO_THRESHOLD).length,
    };
  }, [data.suggestions]);

  const submitBulk = async (items, label) => {
    if (!items.length) {
      toast.info("Aucune affectation à créer");
      return;
    }
    setSubmitting(true);
    try {
      const { data: res } = await api.post("/logibus/affectations/bulk", { items });
      const okCount = res.created || 0;
      const errCount = (res.errors || []).length;
      if (okCount && !errCount) toast.success(`${okCount} affectation(s) créée(s) (${label})`);
      else if (okCount) toast.warning(`${okCount} créée(s), ${errCount} en erreur`);
      else toast.error(`Aucune affectation créée (${errCount} erreur(s))`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally { setSubmitting(false); }
  };

  const acceptSingle = async (vehicle, candidate) => {
    await submitBulk([{
      vehicle_id: vehicle.vehicle_id, route_id: candidate.route_id,
      label: `IA ${Math.round(candidate.confidence * 100)}%`,
      ai_confidence: candidate.confidence,
      ai_reasons: candidate.reasons || [],
    }], `bus ${vehicle.bus_name} → ${candidate.route_short_name}`);
  };

  const acceptHighConfidence = async () => {
    const items = (data.suggestions || []).filter((v) => v.candidates?.[0]?.confidence >= AUTO_THRESHOLD)
      .map((v) => ({ vehicle_id: v.vehicle_id, route_id: v.candidates[0].route_id, label: `IA auto ${Math.round(v.candidates[0].confidence * 100)}%`, ai_confidence: v.candidates[0].confidence, ai_reasons: v.candidates[0].reasons || [] }));
    if (!items.length) { toast.info(`Aucune suggestion ≥ ${Math.round(AUTO_THRESHOLD * 100)}%`); return; }
    await submitBulk(items, `${items.length} bus auto-affectés ≥ ${Math.round(AUTO_THRESHOLD * 100)}%`);
  };

  // ─── Phase 2 — Validation humaine actions ─────────────────────────────────
  const [forceRouteOpen, setForceRouteOpen] = useState(null); // affectation object
  const [whyAiOpen, setWhyAiOpen] = useState(null);           // { explanation, vehicle }
  const [tripsByRoute, setTripsByRoute] = useState({});       // route_id → trip list (lazy)

  const toggleLock = async (vehicle) => {
    const aff = affectationByVehicle[vehicle.vehicle_id];
    if (!aff) { toast.error("Aucune affectation active à verrouiller"); return; }
    const next = !aff.locked;
    try {
      await api.post(`/logibus/affectations/${aff.id}/lock`, { locked: next });
      toast.success(next ? "Affectation verrouillée" : "Verrou retiré");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  const openForceRoute = async (vehicle) => {
    const aff = affectationByVehicle[vehicle.vehicle_id];
    if (!aff) { toast.error("Pas d'affectation active à modifier"); return; }
    setForceRouteOpen({ affectation: aff, vehicle });
  };

  const submitForceRoute = async (affectationId, newTripId, keepLock) => {
    try {
      await api.post(`/logibus/affectations/${affectationId}/force-route`, { new_trip_id: newTripId, keep_lock: keepLock });
      toast.success("Ligne forcée avec succès");
      setForceRouteOpen(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  const openWhyAi = async (vehicle) => {
    const aff = affectationByVehicle[vehicle.vehicle_id];
    if (!aff) { toast.info("Pas d'affectation à expliquer — utilisez le panneau IA pour voir les suggestions."); return; }
    try {
      const { data: exp } = await api.get(`/logibus/affectations/${aff.id}/why`);
      setWhyAiOpen({ explanation: exp, vehicle });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  const handleDragEnd = async ({ over, active }) => {
    if (!over) return;
    const vehicleId = active.data?.current?.vehicleId;
    const routeId = over.data?.current?.routeId;
    if (!vehicleId || !routeId) return;
    const vehicle = (data.suggestions || []).find((v) => v.vehicle_id === vehicleId);
    const route = routes.find((r) => r.route_id === routeId);
    if (!vehicle || !route) return;
    await submitBulk([{ vehicle_id: vehicleId, route_id: routeId, label: `Manuel · ${vehicle.bus_name} → ${route.route_short_name}` }],
      `Manuel : ${vehicle.bus_name} → ${route.route_short_name}`);
  };

  return (
    <div className="p-4 lg:p-6" data-testid="affectations-bulk-page">
      <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
        {/* HEADER */}
        <header className="flex items-end justify-between flex-wrap gap-3 mb-5">
          <div>
            <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">SAEIV · Dispatch intelligent · temps réel</div>
            <h1 className="font-display font-black uppercase text-3xl lg:text-4xl tracking-tighter">Affectations & exploitation</h1>
            <p className="text-sm text-zinc-600 mt-1">
              GPS live → IA multi-signaux → tournée GTFS détectée → validation humaine → dispatch.
              L'affectation statique sert de fallback ; la source de vérité est le GPS temps réel.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={load} disabled={loading} data-testid="refresh-suggestions"
              className="inline-flex items-center gap-1.5 border border-zinc-900 px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-zinc-900 hover:text-white">
              {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Recalculer IA
            </button>
            <button onClick={acceptHighConfidence} disabled={submitting || counters.high === 0}
              data-testid="accept-all-high"
              className="inline-flex items-center gap-1.5 border border-zinc-900 bg-[#FFC60A] px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-zinc-900 hover:text-[#FFC60A] disabled:opacity-50">
              <Sparkles size={14} /> Auto-affecter ≥{Math.round(AUTO_THRESHOLD * 100)}% ({counters.high})
            </button>
          </div>
        </header>

        {/* SAEIV — état flotte temps réel */}
        <div className="border border-zinc-900 mb-3" data-testid="saeiv-summary-bar">
          <div className="bg-zinc-900 text-white px-3 py-1.5 flex items-center justify-between flex-wrap gap-2">
            <div className="text-[10px] font-mono tracking-[0.3em] uppercase">
              <Zap size={10} className="inline" /> SAEIV · Flotte temps réel
            </div>
            <div className="text-[10px] font-mono text-zinc-400">
              {saeiv.total || 0} véhicule{(saeiv.total || 0) > 1 ? "s" : ""} · maj {new Date().toLocaleTimeString("fr-CH", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-6 gap-0">
            {[
              { k: "normal", l: "Normal", color: "bg-green-100" },
              { k: "retard", l: "Retard", color: "bg-orange-100" },
              { k: "hors_tournee", l: "Hors tournée", color: "bg-red-100" },
              { k: "gps_incoherent", l: "GPS incohérent", color: "bg-purple-100" },
              { k: "parked", l: "Stationné", color: "bg-blue-100" },
              { k: "offline", l: "Offline", color: "bg-zinc-100" },
            ].map(({ k, l, color }) => (
              <button key={k}
                data-testid={`saeiv-summary-${k}`}
                onClick={() => setFilter(filter === k ? "all" : k)}
                className={`text-left p-2 border-r border-zinc-900 last:border-r-0 transition-all hover:bg-[#FFC60A] ${color} ${filter === k ? "ring-2 ring-[#FFC60A] ring-inset" : ""}`}>
                <div className="text-[9px] font-mono tracking-widest uppercase opacity-70">{l}</div>
                <div className="font-display font-black text-2xl tracking-tighter">{saeiv.summary?.[k] || 0}</div>
              </button>
            ))}
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-0 border border-zinc-900 mb-5">
          <Kpi label="Non affectés" value={counters.total} testId="kpi-total" />
          <Kpi label="GPS live" value={counters.live} accent="bg-green-500 text-white" testId="kpi-live" />
          <Kpi label="GPS ancien" value={counters.stale} accent="bg-orange-300" testId="kpi-stale" />
          <Kpi label="Offline" value={counters.offline} accent="bg-red-200" testId="kpi-offline" />
          <Kpi label={`Confiance ≥${Math.round(AUTO_THRESHOLD * 100)}%`} value={counters.high} accent="bg-[#FFC60A]" testId="kpi-high" />
        </div>

        {/* FILTERS BAR */}
        <div className="border border-zinc-900 p-3 mb-5 flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 border border-zinc-900 px-2 py-1.5 flex-1 min-w-[200px]">
            <Search size={14} />
            <input data-testid="search-vehicles"
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher un bus (plaque, nom…)"
              className="flex-1 outline-none text-sm" />
          </div>
          {[
            { k: "all", l: "Tous" },
            { k: "unassigned", l: "Non affectés" },
            { k: "live", l: "GPS live" },
            { k: "low", l: "Faible confiance" },
            { k: "offline", l: "Offline" },
          ].map(({ k, l }) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`filter-${k}`}
              className={`px-2.5 py-1.5 border border-zinc-900 text-[10px] font-bold uppercase tracking-widest ${filter === k ? "bg-[#FFC60A]" : "bg-white hover:bg-zinc-100"}`}>
              {l}
            </button>
          ))}
          <select value={routeFilter} onChange={(e) => setRouteFilter(e.target.value)} data-testid="filter-route"
            className="px-2 py-1.5 border border-zinc-900 text-xs font-mono bg-white">
            <option value="">Toutes les lignes</option>
            {routes.map((r) => (
              <option key={r.route_id} value={r.route_id}>{r.route_short_name}</option>
            ))}
          </select>
          <label className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest cursor-pointer" data-testid="toggle-fresh-only">
            <input type="checkbox" checked={freshOnly} onChange={(e) => setFreshOnly(e.target.checked)} />
            GPS frais uniquement
          </label>
        </div>

        {/* GRID : vehicles | routes | detail */}
        <div className="grid lg:grid-cols-12 gap-4">
          {/* LEFT — vehicles list */}
          <section className="lg:col-span-6" data-testid="vehicles-pool">
            <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500 mb-2">
              {filteredVehicles.length} véhicule{filteredVehicles.length > 1 ? "s" : ""} affiché{filteredVehicles.length > 1 ? "s" : ""}
              {data.suggestions?.length > MAX_DISPLAY && ` · ${data.suggestions.length} au total`}
            </div>
            {loading && filteredVehicles.length === 0 ? (
              <div className="border border-dashed border-zinc-300 p-8 text-center text-zinc-500"><Loader2 size={20} className="animate-spin inline" /></div>
            ) : filteredVehicles.length === 0 ? (
              <div className="border border-dashed border-zinc-300 p-8 text-center text-zinc-500 text-sm" data-testid="empty-vehicles">
                Aucun véhicule à dispatcher avec ces filtres.
              </div>
            ) : filteredVehicles.map((v) => (
              <DraggableVehicle key={v.vehicle_id} v={v}
                sv={saeivByVehicle[v.vehicle_id]}
                isSelected={selectedVehicle?.vehicle_id === v.vehicle_id}
                onSelect={setSelectedVehicle}
                onAccept={acceptSingle} />
            ))}
          </section>

          {/* MIDDLE — routes (drop targets) */}
          <section className="lg:col-span-3" data-testid="routes-targets">
            <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500 mb-2">
              {routes.length} ligne{routes.length > 1 ? "s" : ""} cible{routes.length > 1 ? "s" : ""}
            </div>
            {routes.map((r) => (
              <DroppableRoute key={r.route_id} route={r}
                count={busPerRoute[r.route_id] || 0}
                onClick={() => setRouteFilter(routeFilter === r.route_id ? "" : r.route_id)} />
            ))}
          </section>

          {/* RIGHT — detail panel */}
          <aside className="lg:col-span-3" data-testid="vehicle-detail-panel">
            {selectedVehicle ? (
              <VehicleDetailPanel vehicle={selectedVehicle}
                sv={saeivByVehicle[selectedVehicle.vehicle_id]}
                affectation={affectationByVehicle[selectedVehicle.vehicle_id]}
                routes={routes}
                onAccept={(c) => acceptSingle(selectedVehicle, c)}
                onToggleLock={() => toggleLock(selectedVehicle)}
                onForceRoute={() => openForceRoute(selectedVehicle)}
                onWhyAi={() => openWhyAi(selectedVehicle)}
                onClose={() => setSelectedVehicle(null)} />
            ) : (
              <div className="border border-dashed border-zinc-300 p-6 text-center text-zinc-500 text-sm sticky top-4">
                <ArrowRight size={20} className="mx-auto mb-2" />
                Cliquez sur « Détail » pour ouvrir la mini-carte et la fiche complète d'un bus.
              </div>
            )}
          </aside>
        </div>
      </DndContext>

      {/* Phase 2 modals */}
      <ForceRouteModal data={forceRouteOpen} routes={routes}
        onClose={() => setForceRouteOpen(null)}
        onSubmit={submitForceRoute} />
      <WhyAiModal data={whyAiOpen} onClose={() => setWhyAiOpen(null)} />
    </div>
  );
}

function Kpi({ label, value, accent = "", testId }) {
  return (
    <div data-testid={testId} className={`p-3 border-r border-zinc-900 last:border-r-0 ${accent || "bg-white"}`}>
      <div className="text-[10px] font-mono tracking-widest uppercase opacity-70">{label}</div>
      <div className="font-display font-black text-3xl tracking-tighter">{value}</div>
    </div>
  );
}

function VehicleDetailPanel({ vehicle, sv, affectation, routes, onAccept, onToggleLock, onForceRoute, onWhyAi, onClose }) {
  const top = vehicle.candidates?.[0];
  const hasAffectation = !!affectation;
  return (
    <div className="border border-zinc-900 bg-white sticky top-4" data-testid="vehicle-detail">
      <div className="bg-zinc-900 text-white px-3 py-2 flex items-center justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">Détail bus · SAEIV</div>
          <div className="font-display font-bold text-base">{vehicle.bus_name}</div>
        </div>
        <button onClick={onClose} data-testid="close-detail" className="hover:text-[#FFC60A]"><X size={16} /></button>
      </div>
      <VehicleMiniMap vehicle={vehicle} candidate={top} />

      {/* Phase 2 action bar */}
      {hasAffectation && (
        <div className="border-t border-zinc-900 px-3 py-2 bg-zinc-50 flex flex-wrap gap-1.5" data-testid="affectation-actions">
          <button onClick={onToggleLock} data-testid="btn-toggle-lock"
            className="inline-flex items-center gap-1 border border-zinc-900 px-2 py-1 text-[10px] font-bold uppercase tracking-widest hover:bg-zinc-900 hover:text-white">
            {affectation.locked ? <><Unlock size={11} /> Déverrouiller</> : <><Lock size={11} /> Verrouiller</>}
          </button>
          <button onClick={onForceRoute} data-testid="btn-force-route"
            className="inline-flex items-center gap-1 border border-zinc-900 px-2 py-1 text-[10px] font-bold uppercase tracking-widest hover:bg-zinc-900 hover:text-white">
            <Edit3 size={11} /> Forcer ligne
          </button>
          <button onClick={onWhyAi} data-testid="btn-why-ai"
            className="inline-flex items-center gap-1 border border-zinc-900 px-2 py-1 text-[10px] font-bold uppercase tracking-widest hover:bg-zinc-900 hover:text-white">
            <Info size={11} /> Pourquoi IA
          </button>
        </div>
      )}

      <div className="p-3 space-y-2 text-xs">
        <Row label="GPS" value={<GpsBadge status={vehicle.gps_status} />} />
        {sv && <Row label="État" value={<SaeivBadge state={sv.operational_state} />} />}
        {sv && <Row label="Source affectation" value={<SourceBadge source={sv.affectation_source} locked={sv.is_locked} />} />}
        {sv?.confidence != null && <Row label="Confiance IA" value={<ConfidencePill value={sv.confidence} />} />}
        {sv?.detected_route_short_name && <Row label="Ligne détectée" value={<span className="font-display font-black">{sv.detected_route_short_name}</span>} />}
        {sv?.detected_headsign && <Row label="Destination" value={sv.detected_headsign} />}
        {sv?.detected_trip_id && <Row label="Tournée GTFS" value={<span className="font-mono text-[10px]">{sv.detected_trip_id}</span>} />}
        {sv?.next_stop_name && <Row label="Prochain arrêt" value={`${sv.next_stop_name} · ETA ${formatEta(sv.next_stop_eta_seconds)}`} />}
        {sv?.delay_minutes != null && sv.delay_minutes !== 0 && (
          <Row label="Retard estimé" value={
            <span className={sv.delay_minutes > 0 ? "text-orange-700 font-bold" : "text-green-700 font-bold"}>
              {sv.delay_minutes > 0 ? `+${sv.delay_minutes}` : sv.delay_minutes} min
            </span>
          } />
        )}
        {sv?.heading_deg != null && <Row label="Cap" value={`${Math.round(sv.heading_deg)}°`} />}
        {vehicle.gps_age_seconds != null && <Row label="Dernier ping" value={`${Math.floor(vehicle.gps_age_seconds / 60)} min`} />}
        {typeof vehicle.speed === "number" && <Row label="Vitesse" value={`${Math.round(vehicle.speed)} km/h`} />}
        {vehicle.last_detected_route_id && <Row label="Hist. matcher" value={vehicle.last_detected_route_id} />}
        {vehicle.tracker_label && <Row label="Traceur" value={vehicle.tracker_label} />}
      </div>
      {sv?.reasons?.length > 0 && (
        <div className="border-t border-zinc-900 bg-[#FFF7D6] px-3 py-2">
          <div className="text-[10px] font-mono uppercase tracking-widest mb-1"><Brain size={10} className="inline" /> Pourquoi cette détection</div>
          <ul className="text-[10px] font-mono text-zinc-800 ml-3 list-disc space-y-0.5">
            {sv.reasons.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </div>
      )}
      {vehicle.candidates?.length > 0 && (
        <div className="border-t border-zinc-900">
          <div className="bg-[#FFF7D6] px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest">
            <Brain size={10} className="inline" /> Suggestions affectation IA
          </div>
          {vehicle.candidates.map((c, i) => {
            const r = routes.find((rr) => rr.route_id === c.route_id);
            return (
              <div key={c.route_id} className="px-3 py-2 border-t border-zinc-200 first:border-t-0">
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-sm">
                    #{i + 1} Ligne <span className="font-display font-black">{c.route_short_name}</span>
                  </div>
                  <ConfidencePill value={c.confidence} />
                </div>
                <div className="text-[10px] font-mono text-zinc-500 mt-0.5">
                  {r?.route_long_name || ""} · {Math.round(c.distance_m)}m de {c.nearest_stop_name}
                </div>
                {c.reasons?.length > 0 && (
                  <ul className="text-[9px] font-mono text-zinc-600 mt-1 ml-3 list-disc">
                    {c.reasons.map((r) => <li key={r}>{r}</li>)}
                  </ul>
                )}
                <button onClick={() => onAccept(c)} data-testid={`detail-accept-${c.route_id}`}
                  className="mt-1.5 w-full border border-zinc-900 bg-[#FFC60A] py-1 text-[10px] font-bold uppercase tracking-widest hover:bg-zinc-900 hover:text-[#FFC60A]">
                  <CheckCircle2 size={11} className="inline" /> Accepter cette ligne
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Phase 2 modals ─────────────────────────────────────────────────────────
function ForceRouteModal({ data, routes, onClose, onSubmit }) {
  const [routeId, setRouteId] = useState("");
  const [tripId, setTripId] = useState("");
  const [trips, setTrips] = useState([]);
  const [keepLock, setKeepLock] = useState(true);
  useEffect(() => {
    if (!routeId) { setTrips([]); setTripId(""); return; }
    (async () => {
      try {
        const r = await api.get(`/logibus/transit/route/${routeId}/trips`);
        setTrips(r.data || []);
        if ((r.data || []).length) setTripId(r.data[0].trip_id);
      } catch {
        // fallback: just present the route's fallback trip
        setTrips([]);
      }
    })();
  }, [routeId]);

  if (!data) return null;
  return (
    <div className="fixed inset-0 bg-zinc-900/60 z-[2000] flex items-center justify-center p-4" data-testid="force-route-modal" onClick={onClose}>
      <div className="bg-white border-2 border-zinc-900 w-full max-w-lg shadow-[8px_8px_0_0_#0A0A0B]" onClick={(e) => e.stopPropagation()}>
        <div className="bg-zinc-900 text-white px-4 py-3 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">Forcer ligne — override manuel</div>
            <div className="font-display font-bold">{data.vehicle.bus_name}</div>
          </div>
          <button onClick={onClose} data-testid="force-modal-close"><X size={18} /></button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1">Nouvelle ligne</label>
            <select value={routeId} onChange={(e) => setRouteId(e.target.value)} data-testid="force-route-select"
              className="w-full border border-zinc-900 px-2 py-1.5 font-mono text-sm">
              <option value="">— Choisir une ligne —</option>
              {routes.map((r) => (
                <option key={r.route_id} value={r.route_id}>{r.route_short_name} · {r.route_long_name}</option>
              ))}
            </select>
          </div>
          {trips.length > 0 && (
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1">Tournée GTFS (fallback)</label>
              <select value={tripId} onChange={(e) => setTripId(e.target.value)} data-testid="force-trip-select"
                className="w-full border border-zinc-900 px-2 py-1.5 font-mono text-sm">
                {trips.map((t) => (
                  <option key={t.trip_id} value={t.trip_id}>{t.trip_id}{t.trip_headsign ? ` → ${t.trip_headsign}` : ""}</option>
                ))}
              </select>
            </div>
          )}
          <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="keep-lock-toggle">
            <input type="checkbox" checked={keepLock} onChange={(e) => setKeepLock(e.target.checked)} />
            Verrouiller cette affectation après modification (le SAEIV ne pourra plus la changer)
          </label>
        </div>
        <div className="border-t border-zinc-900 p-3 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 border border-zinc-900 text-xs font-bold uppercase tracking-widest hover:bg-zinc-100">Annuler</button>
          <button
            disabled={!tripId}
            onClick={() => onSubmit(data.affectation.id, tripId, keepLock)}
            data-testid="force-route-submit"
            className="px-3 py-1.5 border border-zinc-900 bg-[#FFC60A] text-xs font-bold uppercase tracking-widest hover:bg-zinc-900 hover:text-[#FFC60A] disabled:opacity-50">
            <CheckCircle2 size={12} className="inline" /> Forcer cette ligne
          </button>
        </div>
      </div>
    </div>
  );
}

function WhyAiModal({ data, onClose }) {
  if (!data) return null;
  const { explanation, vehicle } = data;
  return (
    <div className="fixed inset-0 bg-zinc-900/60 z-[2000] flex items-center justify-center p-4" data-testid="why-ai-modal" onClick={onClose}>
      <div className="bg-white border-2 border-zinc-900 w-full max-w-lg shadow-[8px_8px_0_0_#0A0A0B]" onClick={(e) => e.stopPropagation()}>
        <div className="bg-zinc-900 text-white px-4 py-3 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">Pourquoi l'IA a choisi cette ligne</div>
            <div className="font-display font-bold">{vehicle.bus_name}</div>
          </div>
          <button onClick={onClose} data-testid="why-modal-close"><X size={18} /></button>
        </div>
        <div className="p-4 space-y-3 text-sm">
          <Row label="Source" value={<SourceBadge source={explanation.source} locked={explanation.locked} />} />
          <Row label="Tournée GTFS" value={<span className="font-mono text-xs">{explanation.trip_id}</span>} />
          {explanation.ai_confidence != null && (
            <Row label="Confiance IA initiale" value={<ConfidencePill value={explanation.ai_confidence} />} />
          )}
          {explanation.locked && (
            <Row label="Verrou" value={
              <span className="text-[10px] font-mono">par {explanation.locked_by || "—"}<br />{explanation.locked_at?.slice(0, 19).replace("T", " ")}</span>
            } />
          )}
          <Row label="Créé le" value={<span className="font-mono text-[10px]">{explanation.created_at?.slice(0, 19).replace("T", " ")}</span>} />
        </div>
        {explanation.ai_reasons?.length > 0 ? (
          <div className="bg-[#FFF7D6] border-t border-zinc-900 px-4 py-3">
            <div className="text-[10px] font-mono uppercase tracking-widest mb-1"><Brain size={10} className="inline" /> Raisons IA à la création</div>
            <ul className="text-xs ml-4 list-disc space-y-0.5">
              {explanation.ai_reasons.map((r, i) => <li key={i}>{r}</li>)}
            </ul>
          </div>
        ) : (
          <div className="bg-zinc-50 border-t border-zinc-900 px-4 py-3 text-xs text-zinc-600">
            Cette affectation a été créée manuellement — aucune raison IA enregistrée.
          </div>
        )}
        <div className="border-t border-zinc-900 p-3 flex justify-end">
          <button onClick={onClose} className="px-3 py-1.5 border border-zinc-900 text-xs font-bold uppercase tracking-widest hover:bg-zinc-100">Fermer</button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] font-mono tracking-widest uppercase text-zinc-500 w-32 shrink-0">{label}</span>
      <span className="flex-1 text-xs">{value}</span>
    </div>
  );
}
